import * as Yup from 'yup'
import { cloneDeep } from 'lodash-es'
import {
  CampaignListRequest,
  CampaignListParams,
  CHANNEL,
  Campaign
} from './types'

export enum CAMPAIGNSTATUS {
  PROCESS = 'Process',
  SCHEDULE = 'Schedule',
  CANCEL = 'Cancel',
  DRAFT = 'Draft'
}

export enum COMPAIGN_FORM_FIELDS {
  NAME = 'name',
  TEMPLATE_TYPE = 'Type',
  ADVERTISEMENT_NAME = 'advname',
  DESCRIPTION = 'description',
  SCHEDULE_DATE_TIME = 'scheduleDateTime',
  STATUS = 'status',
  PHONE_ID = 'phone_id',
  AUDIENCE = 'audience',
  SCHEDULE_END_DATE = 'scheduleEndDate',
  WHATSAPP_CHANNEL = 'channelId',
  TEMPLATEID = 'templateId',
  ESTIMATEDUSER = 'estimatedUser',
  TEMPLATENAME = 'templateName',
  CHANNEL_TYPE = 'channel',
  LANGUAGE = 'language',
  ADVERTISEMENT = 'advertisement'
}

export interface CompaignForm {
  [COMPAIGN_FORM_FIELDS.NAME]: string;
  [COMPAIGN_FORM_FIELDS.ADVERTISEMENT_NAME]: string;
  [COMPAIGN_FORM_FIELDS.STATUS]: CAMPAIGNSTATUS;
  [COMPAIGN_FORM_FIELDS.DESCRIPTION]: string;
  [COMPAIGN_FORM_FIELDS.WHATSAPP_CHANNEL]: string;
  [COMPAIGN_FORM_FIELDS.PHONE_ID]: string;
  [COMPAIGN_FORM_FIELDS.SCHEDULE_DATE_TIME]: string | Date | null;
  [COMPAIGN_FORM_FIELDS.SCHEDULE_END_DATE]: string | Date | null;
  [COMPAIGN_FORM_FIELDS.ADVERTISEMENT]: { id: number };
  [COMPAIGN_FORM_FIELDS.AUDIENCE]: {
    name: string,
    id: number
  };
  [COMPAIGN_FORM_FIELDS.TEMPLATEID]: string;
  [COMPAIGN_FORM_FIELDS.ESTIMATEDUSER]: number | any;
  [COMPAIGN_FORM_FIELDS.TEMPLATENAME]: string;
  [COMPAIGN_FORM_FIELDS.CHANNEL_TYPE]: CHANNEL;
  [COMPAIGN_FORM_FIELDS.LANGUAGE]: string;
}

export const CompaignFormLabels: Record<string, string> = {
  [COMPAIGN_FORM_FIELDS.NAME]: 'Name',
  [COMPAIGN_FORM_FIELDS.STATUS]: 'Status',
  [COMPAIGN_FORM_FIELDS.DESCRIPTION]: 'Description',
  [COMPAIGN_FORM_FIELDS.SCHEDULE_DATE_TIME]: 'Schedule Date Time',
  [COMPAIGN_FORM_FIELDS.SCHEDULE_END_DATE]: 'End Date Time',
  [COMPAIGN_FORM_FIELDS.AUDIENCE]: 'Audience',
  [COMPAIGN_FORM_FIELDS.TEMPLATEID]: 'Template'
}

export const getInitialFormValuesResponse = (
  response: Campaign
): CompaignForm => {
  const formInitialValues: CompaignForm = {
    [COMPAIGN_FORM_FIELDS.NAME]: response.name ?? '',
    [COMPAIGN_FORM_FIELDS.ADVERTISEMENT_NAME]: response.name ?? '',
    [COMPAIGN_FORM_FIELDS.PHONE_ID]: response.phone_id ?? '',
    [COMPAIGN_FORM_FIELDS.WHATSAPP_CHANNEL]:
      response.template?.channelOnboarding?.channelId ??
      response.channelId ??
      '',
    [COMPAIGN_FORM_FIELDS.STATUS]:
      (response.status as unknown as CAMPAIGNSTATUS) ?? CAMPAIGNSTATUS.DRAFT,

    [COMPAIGN_FORM_FIELDS.CHANNEL_TYPE]:
      (response.channel as CHANNEL) ?? CHANNEL.WHATSAPP,
    [COMPAIGN_FORM_FIELDS.DESCRIPTION]: response.description ?? '',
    [COMPAIGN_FORM_FIELDS.SCHEDULE_DATE_TIME]: response.scheduleDateTime ?? '',
    [COMPAIGN_FORM_FIELDS.AUDIENCE]: {
      name: response.audience?.name ?? '',
      id: response.audience?.id ?? 0
    },
    [COMPAIGN_FORM_FIELDS.TEMPLATEID]: String(
      response?.templateId ?? response?.template?.templateId ?? ''
    ),
    [COMPAIGN_FORM_FIELDS.TEMPLATENAME]:
      response?.template?.name ?? response?.templateName ?? '',
    [COMPAIGN_FORM_FIELDS.LANGUAGE]: '',
    [COMPAIGN_FORM_FIELDS.SCHEDULE_END_DATE]: response.scheduleEndDate ?? '',
    [COMPAIGN_FORM_FIELDS.ADVERTISEMENT]: {
      id: response.advertisement?.id ?? 0
    },
    [COMPAIGN_FORM_FIELDS.ESTIMATEDUSER]: response.estimatedUser ?? 0
  }
  return cloneDeep(formInitialValues)
}

export const defaultCompaignFormValues: CompaignForm = {
  [COMPAIGN_FORM_FIELDS.NAME]: '',
  [COMPAIGN_FORM_FIELDS.STATUS]: CAMPAIGNSTATUS.DRAFT,
  [COMPAIGN_FORM_FIELDS.ADVERTISEMENT_NAME]: '',
  [COMPAIGN_FORM_FIELDS.DESCRIPTION]: '',
  [COMPAIGN_FORM_FIELDS.PHONE_ID]: '',
  [COMPAIGN_FORM_FIELDS.WHATSAPP_CHANNEL]: '',
  [COMPAIGN_FORM_FIELDS.CHANNEL_TYPE]: CHANNEL.WHATSAPP,
  [COMPAIGN_FORM_FIELDS.SCHEDULE_DATE_TIME]: null,
  [COMPAIGN_FORM_FIELDS.SCHEDULE_END_DATE]: null,
  [COMPAIGN_FORM_FIELDS.ADVERTISEMENT]: { id: 0 },
  [COMPAIGN_FORM_FIELDS.AUDIENCE]: { name: '', id: 0 },
  [COMPAIGN_FORM_FIELDS.TEMPLATEID]: '',
  [COMPAIGN_FORM_FIELDS.ESTIMATEDUSER]: 0,
  [COMPAIGN_FORM_FIELDS.TEMPLATENAME]: '',
  [COMPAIGN_FORM_FIELDS.LANGUAGE]: ''
}

export const compaignValidationSchema = Yup.object({
  [COMPAIGN_FORM_FIELDS.NAME]: Yup.string().when(
    COMPAIGN_FORM_FIELDS.CHANNEL_TYPE,
    {
      is: (channel: CHANNEL) =>
        channel === CHANNEL.WHATSAPP ||
        channel === CHANNEL.INAPP ||
        channel === CHANNEL.SMS,
      then: () =>
        Yup.string()
          .required('Campaign name is required')
          .min(3, 'Campaign name must be at least 3 characters long')
          .max(20, 'Campaign name must be at most 20 characters long'),
      otherwise: () => Yup.string().notRequired()
    }
  ),

  [COMPAIGN_FORM_FIELDS.DESCRIPTION]: Yup.string().when(
    COMPAIGN_FORM_FIELDS.CHANNEL_TYPE,
    {
      is: (channel: CHANNEL) =>
        channel === CHANNEL.WHATSAPP ||
        channel === CHANNEL.INAPP ||
        channel === CHANNEL.SMS,
      then: () =>
        Yup.string()
          .required('Description is required')
          .min(10, 'Description must be at least 10 characters long')
          .max(255, 'Description must be at most 255 characters long'),
      otherwise: () => Yup.string().notRequired()
    }
  ),

  [COMPAIGN_FORM_FIELDS.PHONE_ID]: Yup.string().when(
    COMPAIGN_FORM_FIELDS.CHANNEL_TYPE,
    {
      is: CHANNEL.WHATSAPP,
      then: () => Yup.string().required('Phone Id is required'),
      otherwise: () => Yup.string().notRequired()
    }
  ),

  [COMPAIGN_FORM_FIELDS.WHATSAPP_CHANNEL]: Yup.string().when(
    COMPAIGN_FORM_FIELDS.CHANNEL_TYPE,
    {
      is: CHANNEL.WHATSAPP,
      then: () => Yup.string().required('WhatsApp Channel is required'),
      otherwise: () => Yup.string().notRequired()
    }
  ),

  [COMPAIGN_FORM_FIELDS.SCHEDULE_DATE_TIME]: Yup.date()
    .min(
      (() => {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return today
      })(),
      'Schedule Date Time must be today or later'
    )
    .when(COMPAIGN_FORM_FIELDS.CHANNEL_TYPE, {
      is: (channel: string) =>
        [CHANNEL.WHATSAPP, CHANNEL.INAPP, CHANNEL.SMS].includes(
          channel as CHANNEL
        ),
      then: (schema) => schema.required('Schedule Date Time is required'),
      otherwise: (schema) => schema.notRequired()
    }),

  [COMPAIGN_FORM_FIELDS.SCHEDULE_END_DATE]: Yup.string().when(
    COMPAIGN_FORM_FIELDS.CHANNEL_TYPE,
    {
      is: CHANNEL.INAPP,
      then: () => Yup.string().required('End Date Time is required'),
      otherwise: () => Yup.string().notRequired()
    }
  ),
  [COMPAIGN_FORM_FIELDS.AUDIENCE]: Yup.object().when(
    COMPAIGN_FORM_FIELDS.CHANNEL_TYPE,
    {
      is: (channel: CHANNEL) =>
        channel === CHANNEL.WHATSAPP ||
        channel === CHANNEL.INAPP ||
        channel === CHANNEL.SMS,
      then: () =>
        Yup.object()
          .test(
            'audience-required',
            'Audience is required',
            (value: { id?: number }) => !!value?.id
          )
          .required('Audience is required'),
      otherwise: () => Yup.object().notRequired()
    }
  ),

  [COMPAIGN_FORM_FIELDS.TEMPLATEID]: Yup.string().when(
    COMPAIGN_FORM_FIELDS.CHANNEL_TYPE,
    {
      is: (val: CHANNEL) => val === CHANNEL.WHATSAPP || val === CHANNEL.SMS,
      then: () => Yup.string().required('Template is required'),
      otherwise: () => Yup.string().notRequired()
    }
  ),
  [COMPAIGN_FORM_FIELDS.ADVERTISEMENT]: Yup.object().when(
    COMPAIGN_FORM_FIELDS.CHANNEL_TYPE,
    {
      is: CHANNEL.INAPP,
      then: () =>
        Yup.object()
          .test(
            'Template-required',
            'Template is required',
            (value: { id?: number }) => !!value?.id
          )
          .required('Template is required'),
      otherwise: () => Yup.object().notRequired()
    }
  )
})

export const getPaginationQueryParams = (
  request: CampaignListRequest
): CampaignListParams => {
  const { filters, page } = request
  const params: CampaignListParams = {
    size: page?.size ?? 10,
    page: page?.number ? page.number - 1 : 0
  }
  if (filters?.length) {
    filters.forEach(({ fieldName, fieldValue }) => {
      if (
        fieldValue === null ||
        fieldValue === undefined ||
        fieldValue === '' ||
        fieldValue === 'all'
      ) {
        delete params[fieldName]
      } else {
        params[fieldName] = fieldValue
      }
    })
  }
  return params
}
